import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNotify } from "ra-core";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Layers, Upload, Trash2, CopyMinus, Plus, ArrowRightCircle, ArrowLeftCircle, Phone, Pencil } from "lucide-react";
import { useNavigate } from "react-router";
import { ImportWizard } from "./ImportWizard";
import * as api from "./api";
import type { ListRow, RawLeadRow, GridFilters } from "./api";
import { getSupabaseClient } from "../providers/supabase/supabase";
import {
  StatusPill, CaseTypePill, LastCallPill, ScoreDot, Pill, MeToolbar, QuickFilters, SelectMenu,
  ActionsMenu, FilterDrawer, FilterOpenButton, Pager, TabBar, StatTile, SectionCard, formatPhone,
  type ActionGroup,
} from "../me-ui";

// ME → List Stacking (S2 / D50 / D53). Five tabs over the S1 raw-lead spine:
// Dashboard · Property Addresses · Import Log · Tags · Lists. Reads/writes
// raw_leads + the S2 tables only — the live dialer (me_call_queue/leads) is
// untouched. Campaign-assign + send-to-dialer are deferred (S3/S4) and omitted.

// ---- Dashboard tab ----------------------------------------------------------
const DashboardTab = () => {
  const [d, setD] = useState<any>(null);
  useEffect(() => { api.loadDashboard().then(setD); }, []);
  if (!d) return <p className="text-sm text-muted-foreground p-2">Loading…</p>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Total raw leads" value={d.total.toLocaleString()} />
        <StatTile label="New today (vintage)" value={d.newToday.toLocaleString()} />
        <StatTile label="Unassigned to campaign" value={d.unassigned.toLocaleString()} />
        <StatTile label="Promoted to S&A" value={d.promoted.toLocaleString()} />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <SectionCard title="By case type">
          {d.byCase.length === 0 ? <p className="text-sm text-muted-foreground">No data.</p> :
            d.byCase.map(([k, n]: [string, number]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <CaseTypePill caseType={k === "(uncategorized)" ? null : k} />
                <span className="font-semibold tabular-nums">{n.toLocaleString()}</span>
              </div>
            ))}
        </SectionCard>
        <SectionCard title="Top lists">
          {d.byList.length === 0 ? <p className="text-sm text-muted-foreground">No lists yet.</p> :
            d.byList.map((l: any) => (
              <div key={l.title} className="flex justify-between text-sm"><span className="font-medium">{l.title}</span><span className="font-semibold tabular-nums">{l.total_records.toLocaleString()}</span></div>
            ))}
        </SectionCard>
      </div>
    </div>
  );
};

// ---- Property Addresses tab -------------------------------------------------
// A small "pick one option" dialog used by the tag/list/campaign/dialer actions.
const PickerDialog = ({ title, label, options, confirmLabel, onPick, onClose }: {
  title: string; label: string; options: { id: string; label: string }[]; confirmLabel: string;
  onPick: (id: string) => void; onClose: () => void;
}) => {
  const [v, setV] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-1">
          <Label className="text-xs">{label}</Label>
          <Select value={v} onValueChange={setV}>
            <SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>{options.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={!v} onClick={() => onPick(v)}>{confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SAVED_VIEWS_KEY = "me_ls_saved_views";
const emptyFilters: GridFilters = {};

const PropertyAddressesTab = ({ selectedList }: { selectedList: string | null }) => {
  const notify = useNotify();
  const navigate = useNavigate();
  const [rows, setRows] = useState<RawLeadRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState<{ field: string; asc: boolean }>({ field: "vintage", asc: false });
  const [filters, setFilters] = useState<GridFilters>(selectedList ? { listId: selectedList } : {});
  const [draft, setDraft] = useState<GridFilters>(filters);
  const [drawer, setDrawer] = useState(false);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [counts, setCounts] = useState({ all: 0, newWeek: 0, unassigned: 0, neverCalled: 0, dnc: 0 });
  const [listOpts, setListOpts] = useState<{ id: string; title: string }[]>([]);
  const [tagOpts, setTagOpts] = useState<any[]>([]);
  const [campaignOpts, setCampaignOpts] = useState<{ id: string; name: string }[]>([]);
  const [canPush, setCanPush] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showRecord, setShowRecord] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [picker, setPicker] = useState<null | { kind: string }>(null);
  const [savedViews, setSavedViews] = useState<{ name: string; filters: GridFilters }[]>(() => {
    try { return JSON.parse(localStorage.getItem(SAVED_VIEWS_KEY) || "[]"); } catch { return []; }
  });

  const reload = useCallback(() => {
    setLoading(true);
    api.loadRawLeads({ page, pageSize, sort, filters }).then(({ rows, total }) => { setRows(rows); setTotal(total); setLoading(false); });
  }, [page, pageSize, sort, filters]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { api.loadQuickCounts().then(setCounts); }, []);
  useEffect(() => {
    api.loadListOptions().then(setListOpts);
    api.loadTags().then(setTagOpts);
    api.loadCampaignOptions().then(setCampaignOpts);
    api.canPushToAcquisitions().then(setCanPush);
  }, []);
  useEffect(() => { if (selectedList) { setFilters({ listId: selectedList }); setPage(1); } }, [selectedList]);

  // selection ----------------------------------------------------------------
  const clearSel = () => { setSel(new Set()); setAllMatching(false); };
  const applyFilters = (f: GridFilters) => { setFilters(f); setPage(1); clearSel(); };
  const toggleRow = (id: string) => { setAllMatching(false); setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const selectVisible = () => { setAllMatching(false); setSel(new Set(rows.map((r) => r.id))); };
  const selectAllMatching = () => { setAllMatching(true); setSel(new Set(rows.map((r) => r.id))); };
  const headState: "none" | "some" | "all" =
    allMatching || (rows.length > 0 && rows.every((r) => sel.has(r.id))) ? "all" : sel.size > 0 ? "some" : "none";
  const selCount = allMatching ? total : sel.size;
  const hasSel = selCount > 0;
  const targetIds = async (): Promise<string[]> => (allMatching ? api.idsForFilters(filters) : [...sel]);

  // quick filters -------------------------------------------------------------
  const quickPresets: Record<string, GridFilters> = {
    all: {}, newWeek: { newThisWeek: true }, unassigned: { campaignId: "__unassigned__" },
    neverCalled: { neverCalled: true }, dnc: { doNotCall: "only" },
  };
  const activeQuick = filters.newThisWeek ? "newWeek" : filters.campaignId === "__unassigned__" ? "unassigned"
    : filters.neverCalled ? "neverCalled" : filters.doNotCall === "only" ? "dnc"
    : Object.keys(filters).length === 0 ? "all" : "";
  const chips = [
    { key: "all", label: "All ", count: counts.all, active: activeQuick === "all" },
    { key: "newWeek", label: "New this week ", count: counts.newWeek, active: activeQuick === "newWeek" },
    { key: "unassigned", label: "Unassigned ", count: counts.unassigned, active: activeQuick === "unassigned" },
    { key: "neverCalled", label: "Never called ", count: counts.neverCalled, active: activeQuick === "neverCalled" },
    { key: "dnc", label: "DNC ", count: counts.dnc, active: activeQuick === "dnc" },
    ...savedViews.map((v) => ({ key: `view:${v.name}`, label: v.name, active: false })),
    { key: "__save", label: "＋ Saved view", active: false, dashed: true },
  ];
  const onQuick = (key: string) => {
    if (key === "__save") return saveCurrentView();
    if (key.startsWith("view:")) { const v = savedViews.find((x) => `view:${x.name}` === key); if (v) applyFilters(v.filters); return; }
    applyFilters(quickPresets[key] ?? {});
  };
  const saveCurrentView = () => {
    const name = prompt("Name this view:")?.trim(); if (!name) return;
    const next = [...savedViews.filter((v) => v.name !== name), { name, filters }];
    setSavedViews(next); localStorage.setItem(SAVED_VIEWS_KEY, JSON.stringify(next));
    notify(`Saved view "${name}"`, { type: "success" });
  };

  // actions -------------------------------------------------------------------
  const run = async (fn: (ids: string[]) => Promise<{ error?: any } | void>, okMsg: (n: number) => string, confirmMsg?: string) => {
    const ids = await targetIds();
    if (ids.length === 0) return;
    if (confirmMsg && !confirm(confirmMsg.replace("{n}", String(ids.length)))) return;
    const res: any = await fn(ids);
    if (res?.error) return notify(res.error.message, { type: "error" });
    notify(okMsg(ids.length), { type: "success" }); clearSel(); reload();
    api.loadQuickCounts().then(setCounts);
  };
  const pushAll = async () => {
    const ids = await targetIds(); if (!ids.length) return;
    if (!confirm(`Push ${ids.length} raw lead(s) to Acquisitions? They graduate to S&A Leads and leave dialing.`)) return;
    let ok = 0; for (const id of ids) { const { error } = await api.promoteToAcquisitions(id); if (!error) ok++; }
    notify(`Pushed ${ok}/${ids.length} to Acquisitions`, { type: ok ? "success" : "error" }); clearSel(); reload();
  };
  const exportSel = async () => { const ids = await targetIds(); const set = new Set(ids); api.exportSelectedCsv(rows.filter((r) => set.has(r.id))); notify("Exported visible selected rows", { type: "success" }); };

  const onPick = async (kind: string, id: string) => {
    setPicker(null);
    if (kind === "tag-add") return run((ids) => api.addTag(ids, id), (n) => `Tagged ${n}`);
    if (kind === "tag-remove") return run((ids) => api.removeTag(ids, id), (n) => `Removed tag from ${n}`);
    if (kind === "list-add") return run((ids) => api.addToList(ids, id), (n) => `Added ${n} to list`);
    if (kind === "list-remove") return run((ids) => api.removeFromList(ids, id), (n) => `Removed ${n} from list`);
    if (kind === "campaign-add") return run((ids) => api.assignToCampaign(ids, id), (n) => `Assigned ${n} to campaign`);
    if (kind === "dialer") {
      const ids = await targetIds(); if (ids.length) await api.sendToDialer(ids, id);
      notify(`Assigned ${ids.length} — opening the dialer`, { type: "success" }); clearSel();
      navigate("/dialer");
    }
  };

  const groups: ActionGroup[] = [
    { label: "Tags & Lists", items: [
      { label: "Add to tag", icon: "🏷️", onSelect: () => setPicker({ kind: "tag-add" }) },
      { label: "Remove tag", icon: "🏷️", onSelect: () => setPicker({ kind: "tag-remove" }) },
      { label: "Add to list", icon: "🗂️", onSelect: () => setPicker({ kind: "list-add" }) },
      { label: "Remove from list", icon: "🗂️", onSelect: () => setPicker({ kind: "list-remove" }) },
    ] },
    { label: "Marketing", items: [
      { label: "Send to dialer", icon: "📞", onSelect: () => setPicker({ kind: "dialer" }) },
      { label: "Add to campaign", icon: "🎯", onSelect: () => setPicker({ kind: "campaign-add" }) },
    ] },
    { label: "Pipeline", items: [
      { label: "Push to Acquisitions", icon: "⤴️", disabled: !canPush, onSelect: pushAll },
      { label: "Mark Do-Not-Call", icon: "🚫", onSelect: () => run((ids) => api.markDnc(ids), (n) => `Marked ${n} Do-Not-Call`, "Mark {n} lead(s) Do-Not-Call?") },
    ] },
    { label: "Data", items: [
      { label: "Export selected (CSV)", icon: "⬇️", onSelect: exportSel },
      { label: "Delete selected", icon: "🗑️", danger: true, onSelect: () => run(async (ids) => { for (const id of ids) await api.deleteRawLead(id); }, (n) => `Deleted ${n}`, "Delete {n} raw lead(s)?") },
    ] },
  ];

  const pickerCfg: Record<string, { title: string; label: string; confirm: string; options: { id: string; label: string }[] }> = {
    "tag-add": { title: "Add to tag", label: "Tag", confirm: "Add tag", options: tagOpts.map((t) => ({ id: t.id, label: t.name })) },
    "tag-remove": { title: "Remove tag", label: "Tag", confirm: "Remove tag", options: tagOpts.map((t) => ({ id: t.id, label: t.name })) },
    "list-add": { title: "Add to list", label: "List", confirm: "Add to list", options: listOpts.map((l) => ({ id: l.id, label: l.title })) },
    "list-remove": { title: "Remove from list", label: "List", confirm: "Remove", options: listOpts.map((l) => ({ id: l.id, label: l.title })) },
    "campaign-add": { title: "Add to campaign", label: "Campaign", confirm: "Assign", options: campaignOpts.map((c) => ({ id: c.id, label: c.name })) },
    "dialer": { title: "Send to dialer", label: "Campaign to dial", confirm: "Assign & open dialer", options: campaignOpts.map((c) => ({ id: c.id, label: c.name })) },
  };

  const sortBy = (field: string) => setSort((s) => ({ field, asc: s.field === field ? !s.asc : false }));
  const arrow = (field: string) => sort.field === field ? (sort.asc ? " ↑" : " ↓") : "";

  // S3: full-page detail replaces the grid in place (prototype pattern) — no modal.
  if (detailId) return (
    <RawLeadDetail id={detailId} onBack={() => { setDetailId(null); reload(); }} onChanged={reload}
      siblings={rows.map((r) => r.id)} onNav={setDetailId} />
  );

  return (
    <div className="space-y-1">
      <QuickFilters chips={chips as any} onToggle={onQuick} />

      <MeToolbar>
        <SelectMenu headState={headState} visibleCount={rows.length} allCount={total}
          onSelectVisible={selectVisible} onSelectAll={selectAllMatching} onClear={clearSel} />
        <ActionsMenu groups={groups} disabled={!hasSel} />
        <span className="text-sm text-muted-foreground font-medium">
          {hasSel
            ? <>{allMatching ? <>All <b className="text-foreground">{total.toLocaleString()}</b> selected</> : <><b className="text-foreground">{sel.size}</b> selected{!allMatching && sel.size > 0 && <> · <button className="text-primary" onClick={selectAllMatching}>select all {total.toLocaleString()}</button></>}</>}</>
            : "No rows selected"}
        </span>
        <div className="flex-1" />
        <FilterOpenButton onClick={() => { setDraft(filters); setDrawer(true); }} />
        <Button variant="outline" size="sm" className="h-9" onClick={() => setShowRecord(true)}><Plus className="mr-1 h-4 w-4" />Add Record</Button>
        <Button variant="outline" size="sm" className="h-9" onClick={() => setShowImport(true)}><Upload className="mr-1 h-4 w-4" />Import</Button>
        <Button variant="outline" size="sm" className="h-9" onClick={() => api.dedupeByMailing(filters.listId ?? null).then(({ data }: any) => { notify(`${data ?? 0} duplicates collapsed`, { type: "success" }); reload(); })}><CopyMinus className="mr-1 h-4 w-4" />Dedupe</Button>
      </MeToolbar>

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-9"></TableHead>
                <TableHead>Property Address</TableHead>
                <TableHead>Case Type</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead>List · Tags</TableHead>
                <TableHead>Last Call</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => sortBy("attempts")}>Attempts{arrow("attempts")}</TableHead>
                <TableHead>Best Phone</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => sortBy("vintage")}>Vintage{arrow("vintage")}</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => sortBy("status")}>Status{arrow("status")}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-8">No raw leads match.</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} className={`cursor-pointer ${sel.has(r.id) ? "bg-blue-50/60 dark:bg-blue-950/20" : ""}`}>
                  <TableCell onClick={(e) => e.stopPropagation()}><Checkbox checked={sel.has(r.id)} onCheckedChange={() => toggleRow(r.id)} /></TableCell>
                  <TableCell onClick={() => setDetailId(r.id)}>
                    <div className="font-semibold text-blue-700 dark:text-blue-400">{r.property_address}</div>
                    <div className="text-xs text-muted-foreground">{r.owner}{r.in_leads && <span className="ml-1 text-emerald-700">· In Leads</span>}</div>
                  </TableCell>
                  <TableCell><CaseTypePill caseType={r.case_type} /></TableCell>
                  <TableCell className="text-sm">{r.campaign_name ?? <span className="text-muted-foreground">Unassigned</span>}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.list_count} <span className="opacity-60">·</span> {r.tag_count} tags</TableCell>
                  <TableCell><LastCallPill value={r.last_call} /></TableCell>
                  <TableCell className="text-center text-sm">{r.call_attempts}</TableCell>
                  <TableCell><span className="inline-flex items-center gap-1.5"><ScoreDot score={r.best_score} /><span className="text-sm font-mono">{formatPhone(r.best_number)}</span></span></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.vintage_date ?? "—"}</TableCell>
                  <TableCell><StatusPill status={r.record_status} /></TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2 text-muted-foreground">
                      <button title="Send to dialer" onClick={() => setPicker({ kind: "dialer" })}><Phone className="h-4 w-4 hover:text-blue-600" /></button>
                      <button title="Delete" onClick={async () => { if (confirm("Delete this raw lead?")) { await api.deleteRawLead(r.id); reload(); } }}><Trash2 className="h-4 w-4 hover:text-red-600" /></button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} onPageSize={(n) => { setPageSize(n); setPage(1); }} />
      </Card>

      <FilterDrawer open={drawer} onOpenChange={setDrawer}
        onReset={() => setDraft(emptyFilters)}
        onApply={() => { applyFilters(draft); setDrawer(false); }}>
        <FilterField label="Case type">
          <select className="w-full h-9 rounded-md border bg-background px-2 text-sm" value={draft.caseType ?? ""} onChange={(e) => setDraft({ ...draft, caseType: e.target.value || null })}>
            <option value="">Any</option>{["probate", "foreclosure", "divorce", "absentee", "mls"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </FilterField>
        <FilterField label="List">
          <select className="w-full h-9 rounded-md border bg-background px-2 text-sm" value={draft.listId ?? ""} onChange={(e) => setDraft({ ...draft, listId: e.target.value || null })}>
            <option value="">Any list</option>{listOpts.map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
          </select>
        </FilterField>
        <FilterField label="Tag">
          <select className="w-full h-9 rounded-md border bg-background px-2 text-sm" value={draft.tagId ?? ""} onChange={(e) => setDraft({ ...draft, tagId: e.target.value || null })}>
            <option value="">Any</option>{tagOpts.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </FilterField>
        <FilterField label="Campaign">
          <select className="w-full h-9 rounded-md border bg-background px-2 text-sm" value={draft.campaignId ?? ""} onChange={(e) => setDraft({ ...draft, campaignId: e.target.value || null })}>
            <option value="">Any</option><option value="__unassigned__">Unassigned</option>{campaignOpts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </FilterField>
        <FilterField label="Last call status">
          <select className="w-full h-9 rounded-md border bg-background px-2 text-sm" value={draft.lastCall ?? ""} onChange={(e) => setDraft({ ...draft, lastCall: e.target.value || null })}>
            <option value="">Any</option>{["connected", "no_answer", "voicemail", "wrong", "dead"].map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
          </select>
        </FilterField>
        <FilterField label="Vintage from"><Input type="date" value={draft.vintageFrom ?? ""} onChange={(e) => setDraft({ ...draft, vintageFrom: e.target.value || null })} className="h-9" /></FilterField>
        <FilterField label="Vintage to"><Input type="date" value={draft.vintageTo ?? ""} onChange={(e) => setDraft({ ...draft, vintageTo: e.target.value || null })} className="h-9" /></FilterField>
        <FilterField label="Do-not-call">
          <select className="w-full h-9 rounded-md border bg-background px-2 text-sm" value={draft.doNotCall ?? ""} onChange={(e) => setDraft({ ...draft, doNotCall: (e.target.value || null) as any })}>
            <option value="">Any</option><option value="exclude">Exclude DNC</option><option value="only">Only DNC</option>
          </select>
        </FilterField>
      </FilterDrawer>

      {picker && pickerCfg[picker.kind] && (
        <PickerDialog title={pickerCfg[picker.kind].title} label={pickerCfg[picker.kind].label} confirmLabel={pickerCfg[picker.kind].confirm}
          options={pickerCfg[picker.kind].options} onPick={(id) => onPick(picker.kind, id)} onClose={() => setPicker(null)} />
      )}
      <ImportWizard open={showImport} onOpenChange={setShowImport} defaultListId={filters.listId ?? null} onDone={reload} />
      <AddRecordDialog open={showRecord} onOpenChange={setShowRecord} listId={filters.listId ?? null} onDone={reload} />
    </div>
  );
};

const FilterField = ({ label, children }: { label: string; children: ReactNode }) => (
  <div><Label className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5 block">{label}</Label>{children}</div>
);

// ---- Add Record dialog (single raw lead via the importer RPC) ---------------
const AddRecordDialog = ({
  open, onOpenChange, listId, onDone,
}: { open: boolean; onOpenChange: (v: boolean) => void; listId: string | null; onDone: () => void }) => {
  const notify = useNotify();
  const [f, setF] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const set = (k: string) => (e: any) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    const payload: any = { filename: "manual-add", dedupe: "skip", rows: [f] };
    if (listId) payload.list_id = listId;
    const { data, error } = await getSupabaseClient().rpc("import_raw_leads", { payload });
    setSaving(false);
    if (error) return notify(error.message, { type: "error" });
    notify(data?.rows_imported ? "Record added" : "Record already existed (deduped)", { type: "success" });
    setF({}); onOpenChange(false); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add a raw lead</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {[["address_line1", "Property Address"], ["city", "City"], ["county", "County"], ["zip", "Zip"],
            ["owner_1_name", "Owner Name"], ["first_name", "Contact First"], ["last_name", "Contact Last"], ["phone", "Phone"]].map(([k, label]) => (
            <div key={k} className="space-y-1">
              <Label className="text-xs">{label}</Label>
              <Input value={f[k] ?? ""} onChange={set(k)} className="h-8" />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || !f.address_line1}>{saving ? "Saving…" : "Add record"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- RawLeadShow (basic detail dialog) --------------------------------------
// ---- Full-page marketing detail (S3 / D64) ----------------------------------
const KV = ({ k, v }: { k: string; v: ReactNode }) => (
  <div className="flex justify-between gap-3 py-1.5 border-b border-border/60 last:border-0 text-sm">
    <span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{v ?? "—"}</span>
  </div>
);
const Panel = ({ title, extra, children }: { title: ReactNode; extra?: ReactNode; children: ReactNode }) => (
  <Card className="p-0"><CardContent className="p-4">
    <div className="flex items-center justify-between mb-3"><h3 className="text-sm font-semibold">{title}</h3>{extra}</div>
    {children}
  </CardContent></Card>
);

const RawLeadDetail = ({ id, onBack, onChanged, siblings, onNav }: {
  id: string; onBack: () => void; onChanged?: () => void; siblings?: string[]; onNav?: (id: string) => void;
}) => {
  const notify = useNotify();
  const navigate = useNavigate();
  const [d, setD] = useState<import("./api").RawLeadDetail | null>(null);
  const [tab, setTab] = useState("overview");
  const [canPush, setCanPush] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [comms, setComms] = useState<any[]>([]);
  const [commFilter, setCommFilter] = useState<string>("all");
  const [listOpts, setListOpts] = useState<{ id: string; title: string }[]>([]);
  const [tagOpts, setTagOpts] = useState<any[]>([]);
  const [campaignOpts, setCampaignOpts] = useState<{ id: string; name: string }[]>([]);
  const [picker, setPicker] = useState<null | { kind: string }>(null);

  const reload = () => api.loadRawLeadDetail(id).then((x) => { setD(x); if (x?.raw?.source_lead_id) api.loadRawLeadComms(x.raw.source_lead_id).then(setComms); else setComms([]); });
  useEffect(() => { setTab("overview"); reload(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { api.canPushToAcquisitions().then(setCanPush); api.loadListOptions().then(setListOpts); api.loadTags().then(setTagOpts); api.loadCampaignOptions().then(setCampaignOpts); }, []);

  if (!d) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  const r = d.raw, p = d.property;
  const promoted = r.record_status === "promoted";
  const addr = [p.address_line1, p.city, p.state, p.zip].filter(Boolean).join(", ") || "—";
  const changed = () => { reload(); onChanged?.(); };
  const idx = siblings?.indexOf(id) ?? -1;

  const push = async () => { setBusy(true); const { error } = await api.promoteToAcquisitions(id); setBusy(false); if (error) return notify(api.humanError(error), { type: "error" }); notify("Pushed to Acquisitions — now an S&A Lead", { type: "success" }); changed(); };
  const demote = async () => { if (!confirm("Return this lead to Marketing? It re-enters the dial queues.")) return; setBusy(true); const { error } = await api.demoteFromAcquisitions(id); setBusy(false); if (error) return notify(api.humanError(error), { type: "error" }); notify("Returned to Marketing", { type: "success" }); changed(); };
  const logNote = async () => { if (!note.trim() || !r.source_lead_id) return; const { error } = await api.addNote(r.source_lead_id, note.trim()); if (error) return notify(api.humanError(error), { type: "error" }); setNote(""); notify("Note logged", { type: "success" }); reload(); };
  const toggleFlag = async (k: "do_not_call" | "do_not_mail", v: boolean) => { const { error } = await api.setRawLeadFlags(id, { [k]: v }); if (error) return notify(api.humanError(error), { type: "error" }); changed(); };
  const doRemoveList = async (lid: string) => { const { error } = await api.removeFromList([id], lid); if (error) return notify(api.humanError(error), { type: "error" }); changed(); };
  const doRemoveTag = async (tid: string) => { const { error } = await api.removeTag([id], tid); if (error) return notify(api.humanError(error), { type: "error" }); changed(); };
  const onPick = async (kind: string, pid: string) => {
    setPicker(null);
    const fns: Record<string, () => Promise<any>> = {
      "list-add": () => api.addToList([id], pid), "tag-add": () => api.addTag([id], pid),
      "campaign-add": () => api.assignToCampaign([id], pid),
      "dialer": async () => { await api.sendToDialer([id], pid); notify("Assigned — opening dialer", { type: "success" }); navigate("/dialer"); },
    };
    const { error }: any = (await fns[kind]?.()) ?? {};
    if (error) return notify(api.humanError(error), { type: "error" });
    if (kind !== "dialer") { notify("Done", { type: "success" }); changed(); }
  };
  const pickerCfg: Record<string, { title: string; label: string; options: { id: string; label: string }[] }> = {
    "list-add": { title: "Add to list", label: "List", options: listOpts.map((l) => ({ id: l.id, label: l.title })) },
    "tag-add": { title: "Add tag", label: "Tag", options: tagOpts.map((t) => ({ id: t.id, label: t.name })) },
    "campaign-add": { title: "Add to campaign", label: "Campaign", options: campaignOpts.map((c) => ({ id: c.id, label: c.name })) },
    "dialer": { title: "Send to dialer", label: "Campaign to dial", options: campaignOpts.map((c) => ({ id: c.id, label: c.name })) },
  };
  const actionGroups: ActionGroup[] = [
    { label: "Tags & Lists", items: [
      { label: "Add to tag", icon: "🏷️", onSelect: () => setPicker({ kind: "tag-add" }) },
      { label: "Add to list", icon: "🗂️", onSelect: () => setPicker({ kind: "list-add" }) },
      { label: "Add to campaign", icon: "🎯", onSelect: () => setPicker({ kind: "campaign-add" }) },
    ] },
    { label: "Data", items: [
      { label: r.do_not_call ? "Un-mark Do-Not-Call" : "Mark Do-Not-Call", icon: "🚫", onSelect: () => toggleFlag("do_not_call", !r.do_not_call) },
    ] },
  ];

  const CommRow = ({ a }: { a: any }) => (
    <li className="flex gap-2 text-sm border-b border-border/60 pb-2">
      <Badge variant="outline" className="shrink-0 capitalize h-5">{a.type}</Badge>
      <div className="flex-1"><div>{a.body || a.disposition || "—"}</div>
        <div className="text-xs text-muted-foreground">{a.direction !== "none" ? `${a.direction} · ` : ""}{a.duration_sec ? `${a.duration_sec}s · ` : ""}{new Date(a.occurred_at).toLocaleString()}</div></div>
    </li>
  );
  const shownComms = commFilter === "all" ? comms : comms.filter((a) => a.type === commFilter);
  const NoteComposer = () => (
    <div className="space-y-2">
      <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Log a note…" />
      <div className="flex justify-end"><Button size="sm" onClick={logNote} disabled={!note.trim()}>Log note</Button></div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* header */}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} className="text-teal-700 dark:text-teal-400 font-bold flex items-center gap-1.5"><Layers className="h-4 w-4" /> List Stacking ›</button>
        <span className="font-semibold text-lg">{addr}</span>
        <div className="flex gap-1.5">
          <StatusPill status={r.record_status} />
          {r.case_type && <CaseTypePill caseType={r.case_type} />}
          {d.campaign && <Pill tone="grey">{d.campaign.name}</Pill>}
          {r.do_not_call && <Pill tone="red">DNC</Pill>}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {promoted
            ? canPush && <Button size="sm" variant="outline" disabled={busy} onClick={demote}><ArrowLeftCircle className="mr-1 h-4 w-4" />Return to Marketing</Button>
            : canPush && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={push}><ArrowRightCircle className="mr-1 h-4 w-4" />Push to Acquisitions</Button>}
          <Button size="sm" variant="outline" onClick={() => setPicker({ kind: "dialer" })}><Phone className="mr-1 h-4 w-4" />Send to dialer</Button>
          <ActionsMenu groups={actionGroups} />
          {siblings && idx >= 0 && (
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" disabled={idx <= 0} onClick={() => onNav?.(siblings[idx - 1])}>‹</Button>
              <Button size="sm" variant="ghost" disabled={idx >= siblings.length - 1} onClick={() => onNav?.(siblings[idx + 1])}>›</Button>
            </div>
          )}
          <Button size="icon" variant="ghost" onClick={onBack}>✕</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-4 items-start">
        <div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-5 h-10 max-w-2xl">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="comm">Communication</TabsTrigger>
              <TabsTrigger value="marketing">Marketing</TabsTrigger>
              <TabsTrigger value="liststags">Lists &amp; Tags</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-3 space-y-4">
              <Panel title={<span>📇 Contact Information <span className="text-muted-foreground font-normal">· {d.phones.length} phones · {d.emails.length} email</span></span>}>
                {d.phones.length === 0 ? <p className="text-sm text-muted-foreground">No phones.</p> :
                  <div className="space-y-2">{d.phones.map((ph: any) => (
                    <div key={ph.id} className="flex items-center gap-3 py-1.5 border-b border-border/60 last:border-0">
                      <div className="grid h-8 w-8 place-items-center rounded-md bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">{ph.phone_type === "landline" ? "☎️" : "📱"}</div>
                      <div className="flex-1"><div className="font-semibold text-sm font-mono">{formatPhone(ph.number_e164)}{ph.is_primary ? " ★" : ""}</div>
                        <div className="text-xs text-muted-foreground capitalize">{ph.phone_type ?? "phone"} · {ph.status ?? "not yet tried"}{ph.dnc ? " · DNC" : ""}</div></div>
                      <div className="text-right"><span className="inline-flex items-center gap-1.5"><ScoreDot score={ph.activity_score} /><span className="font-bold text-sm">{ph.activity_score ?? "—"}</span></span><div className="text-[10px] text-muted-foreground">score</div></div>
                    </div>))}
                    {d.emails.map((e) => <div key={e} className="flex items-center gap-3 py-1.5"><div className="grid h-8 w-8 place-items-center rounded-md bg-slate-100 text-slate-500">✉️</div><div className="text-sm font-semibold">{e}</div></div>)}
                  </div>}
              </Panel>
              <Panel title="💬 Communication & Notes">
                {comms.length === 0
                  ? <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">No calls, texts or notes yet — this lead hasn't been worked.</div>
                  : <ul className="space-y-2 mb-3 max-h-52 overflow-auto">{comms.slice(0, 5).map((a) => <CommRow key={a.id} a={a} />)}</ul>}
                <NoteComposer />
              </Panel>
              <Panel title={<span>🏠 Property &amp; Owner <span className="text-muted-foreground font-normal">· marketing context</span></span>}>
                <KV k="Owner(s)" v={[p.owner_1_name, p.owner_2_name].filter(Boolean).join(" · ") || [d.contact.first_name, d.contact.last_name].filter(Boolean).join(" ")} />
                <KV k="Property type" v={p.property_type} />
                <KV k="County · source" v={[r.county ?? p.county, r.source_channel ?? p.source].filter(Boolean).join(" · ")} />
                <KV k="Mailing address" v={p.mailing_address} />
                <KV k="APN / folio" v={p.apn_folio} />
                <div className="mt-3 rounded-md border border-dashed bg-muted/40 p-3 text-xs text-muted-foreground">
                  💡 Valuation, equity, liens &amp; underwriting live on the <b className="text-foreground">Acquisitions lead</b> — available once this raw lead is pushed to Acquisitions.
                  {promoted && r.promoted_lead_id && <> <a className="text-blue-700 dark:text-blue-400 font-semibold" href={`/#/leads/${r.promoted_lead_id}/show`}>Open the S&amp;A lead →</a></>}
                </div>
              </Panel>
            </TabsContent>

            <TabsContent value="comm" className="mt-3">
              <Panel title="💬 Communication thread" extra={
                <select className="h-8 rounded-md border bg-background px-2 text-xs" value={commFilter} onChange={(e) => setCommFilter(e.target.value)}>
                  {["all", "call", "sms", "note"].map((f) => <option key={f} value={f}>{f}</option>)}
                </select>}>
                {shownComms.length === 0 ? <p className="text-sm text-muted-foreground mb-3">No {commFilter === "all" ? "" : commFilter} activity.</p> :
                  <ul className="space-y-2 mb-3 max-h-[50vh] overflow-auto">{shownComms.map((a) => <CommRow key={a.id} a={a} />)}</ul>}
                <NoteComposer />
              </Panel>
            </TabsContent>

            <TabsContent value="marketing" className="mt-3">
              <Panel title="🎯 Marketing status">
                <KV k="Campaign" v={d.campaign?.name ?? "Unassigned"} />
                <KV k="Record status" v={<span className="capitalize">{r.record_status.replace("_", " ")}</span>} />
                <KV k="Call attempts" v={r.call_attempts} /><KV k="SMS attempts" v={r.sms_attempts} /><KV k="Mail step" v={r.mail_step} />
                <KV k="Next action" v={r.next_action_at ? new Date(r.next_action_at).toLocaleString() : "Due now"} />
                <KV k="Reactivation" v={r.reactivation_at ?? "—"} />
                <KV k="Vintage" v={r.vintage_date} />
                <div className="flex gap-6 mt-3">
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!r.do_not_call} onCheckedChange={(v) => toggleFlag("do_not_call", !!v)} /> Do-not-call</label>
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!r.do_not_mail} onCheckedChange={(v) => toggleFlag("do_not_mail", !!v)} /> Do-not-mail</label>
                </div>
              </Panel>
            </TabsContent>

            <TabsContent value="liststags" className="mt-3 space-y-4">
              <Panel title="🗂️ Lists" extra={<Button size="sm" variant="outline" onClick={() => setPicker({ kind: "list-add" })}><Plus className="mr-1 h-3 w-3" />Add to list</Button>}>
                {d.lists.length === 0 ? <p className="text-sm text-muted-foreground">Not on any list.</p> :
                  <div className="flex flex-wrap gap-2">{d.lists.map((l) => <span key={l.id} className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold">{l.title}<button onClick={() => doRemoveList(l.id)} className="text-muted-foreground hover:text-red-600">✕</button></span>)}</div>}
              </Panel>
              <Panel title="🏷️ Tags" extra={<Button size="sm" variant="outline" onClick={() => setPicker({ kind: "tag-add" })}><Plus className="mr-1 h-3 w-3" />Add tag</Button>}>
                {d.tags.length === 0 ? <p className="text-sm text-muted-foreground">No tags.</p> :
                  <div className="flex flex-wrap gap-2">{d.tags.map((t) => <span key={t.id} className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold"><span className="h-2 w-2 rounded-full" style={{ background: t.color || "#94a3b8" }} />{t.name}<button onClick={() => doRemoveTag(t.id)} className="text-muted-foreground hover:text-red-600">✕</button></span>)}</div>}
              </Panel>
            </TabsContent>

            <TabsContent value="activity" className="mt-3">
              <Panel title="🕑 Activity timeline">
                {comms.length === 0 ? <p className="text-sm text-muted-foreground">No activity yet.</p> :
                  <ul className="space-y-2 max-h-[55vh] overflow-auto">{comms.map((a) => <CommRow key={a.id} a={a} />)}</ul>}
              </Panel>
            </TabsContent>
          </Tabs>
        </div>

        {/* right rail */}
        <div className="space-y-4">
          <Panel title="Marketing status">
            <KV k="Campaign" v={d.campaign?.name ?? "Unassigned"} />
            <KV k="Record status" v={<span className="capitalize">{r.record_status.replace("_", " ")}</span>} />
            <KV k="Attempts" v={`${r.call_attempts} calls · ${r.sms_attempts} sms`} />
            <KV k="Next action" v={r.next_action_at ? new Date(r.next_action_at).toLocaleDateString() : "Due now"} />
            <KV k="Vintage" v={r.vintage_date} />
            <KV k="Do-not-call" v={r.do_not_call ? "Yes" : "No"} />
          </Panel>
          <Panel title="Lists" extra={<button className="text-blue-700 dark:text-blue-400 text-xs font-semibold" onClick={() => setPicker({ kind: "list-add" })}>＋ Add</button>}>
            {d.lists.length === 0 ? <p className="text-xs text-muted-foreground">—</p> :
              <div className="flex flex-wrap gap-1.5">{d.lists.map((l) => <Badge key={l.id} variant="outline">{l.title}</Badge>)}</div>}
          </Panel>
          <Panel title="Tags" extra={<button className="text-blue-700 dark:text-blue-400 text-xs font-semibold" onClick={() => setPicker({ kind: "tag-add" })}>＋ Add</button>}>
            {d.tags.length === 0 ? <p className="text-xs text-muted-foreground">—</p> :
              <div className="flex flex-wrap gap-1.5">{d.tags.map((t) => <Badge key={t.id} variant="outline"><span className="h-2 w-2 rounded-full mr-1" style={{ background: t.color || "#94a3b8" }} />{t.name}</Badge>)}</div>}
          </Panel>
        </div>
      </div>

      {picker && pickerCfg[picker.kind] && (
        <PickerDialog title={pickerCfg[picker.kind].title} label={pickerCfg[picker.kind].label} confirmLabel="Apply"
          options={pickerCfg[picker.kind].options} onPick={(pid) => onPick(picker.kind, pid)} onClose={() => setPicker(null)} />
      )}
    </div>
  );
};

// ---- Import Log tab ---------------------------------------------------------
const ImportLogTab = () => {
  const [rows, setRows] = useState<any[]>([]);
  useEffect(() => { api.loadImportLog().then(setRows); }, []);
  return (
    <Card className="overflow-hidden p-0"><div className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Source</TableHead><TableHead>When</TableHead><TableHead>File / Run</TableHead>
          <TableHead>Rows</TableHead><TableHead>Imported</TableHead><TableHead>Dupes</TableHead><TableHead>DNC</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No imports yet.</TableCell></TableRow>
          ) : rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell><Pill tone={r.source === "manual" ? "blue" : "violet"}>{r.source === "manual" ? "Manual" : "DMStack"}</Pill></TableCell>
              <TableCell className="text-xs text-muted-foreground">{r.at ? new Date(r.at).toLocaleString() : "—"}</TableCell>
              <TableCell className="font-medium">{r.filename}</TableCell>
              <TableCell className="tabular-nums">{r.total ?? "—"}</TableCell>
              <TableCell className="tabular-nums">{r.imported ?? "—"}</TableCell>
              <TableCell className="tabular-nums">{r.dupes ?? "—"}</TableCell>
              <TableCell className="tabular-nums">{r.dnc ?? "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div></Card>
  );
};

// ---- Tags tab ---------------------------------------------------------------
// Banner shown when the current role can't manage tags/lists — the clear message
// that replaces the old silent/greyed "not active" create (the reported defect).
const PermBanner = () => (
  <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300 text-sm px-3 py-2">
    You have read-only access. Managing tags &amp; lists needs the <b>Tags/Lists management</b> permission — ask an Admin to set your role to <b>Admin</b> or <b>Lead Manager</b> in Settings → Roles.
  </div>
);

const SWATCHES = ["#2563eb", "#0e9384", "#6d28d9", "#b45309", "#15803d", "#b91c1c", "#334155"];

const TagEditor = ({ tag, onClose, onSaved }: { tag: any | "new"; onClose: () => void; onSaved: () => void }) => {
  const notify = useNotify();
  const isNew = tag === "new";
  const [name, setName] = useState(isNew ? "" : tag.name ?? "");
  const [category, setCategory] = useState(isNew ? "" : tag.category ?? "");
  const [color, setColor] = useState(isNew ? SWATCHES[0] : tag.color ?? SWATCHES[0]);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    const { error } = isNew
      ? await api.createTag(name.trim(), category.trim() || null, color)
      : await api.updateTag(tag.id, { name: name.trim(), category: category.trim() || null, color });
    setBusy(false);
    if (error) return notify(api.humanError(error), { type: "error" });
    notify(isNew ? "Tag created" : "Tag saved", { type: "success" });
    onSaved(); onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>{isNew ? "New tag" : "Edit tag"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} autoFocus /></div>
          <div className="space-y-1"><Label className="text-xs">Category (optional)</Label><Input value={category} onChange={(e) => setCategory(e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Color</Label>
            <div className="flex gap-1.5">{SWATCHES.map((s) => (
              <button key={s} onClick={() => setColor(s)} className={`h-6 w-6 rounded-full border-2 ${color === s ? "border-foreground" : "border-transparent"}`} style={{ background: s }} />
            ))}</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !name.trim()}>{busy ? "Saving…" : isNew ? "Create tag" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TagsTab = () => {
  const notify = useNotify();
  const [tags, setTags] = useState<any[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [editing, setEditing] = useState<any | "new" | null>(null);
  const reload = () => api.loadTags().then(setTags);
  useEffect(() => { reload(); api.canManageTagsLists().then(setCanManage); }, []);
  const del = async (t: any) => {
    const msg = t.raw_lead_uses > 0
      ? `"${t.name}" is used on ${t.raw_lead_uses} lead(s) — deleting it will untag them. Continue?`
      : `Delete tag "${t.name}"?`;
    if (!confirm(msg)) return;
    const { error } = await api.deleteTag(t.id);
    if (error) return notify(api.humanError(error), { type: "error" });
    notify("Tag deleted", { type: "success" }); reload();
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">Tags are filter labels on raw leads (D55).</div>
        {canManage && <Button size="sm" onClick={() => setEditing("new")}><Plus className="mr-1 h-4 w-4" />New tag</Button>}
      </div>
      {!canManage && <PermBanner />}
      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>Color</TableHead><TableHead>Usage</TableHead>{canManage && <TableHead></TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {tags.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No tags yet.</TableCell></TableRow> :
              tags.map((t) => (
                <TableRow key={t.id}>
                  <TableCell><span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: t.color || "#94a3b8" }} /><span className="font-medium">{t.name}</span></span></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{t.category ?? "—"}</TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground">{t.color ?? "—"}</TableCell>
                  <TableCell className="text-sm">{t.raw_lead_uses} lead{t.raw_lead_uses === 1 ? "" : "s"}</TableCell>
                  {canManage && <TableCell><div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => setEditing(t)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(t)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </div></TableCell>}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>
      {editing !== null && <TagEditor tag={editing} onClose={() => setEditing(null)} onSaved={reload} />}
    </div>
  );
};

// ---- Lists tab --------------------------------------------------------------
const ListEditor = ({ list, onClose, onSaved }: { list: any | "new"; onClose: () => void; onSaved: () => void }) => {
  const notify = useNotify();
  const isNew = list === "new";
  const [title, setTitle] = useState(isNew ? "" : list.title ?? "");
  const [caseType, setCaseType] = useState(isNew ? "" : list.case_type ?? "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!title.trim()) return;
    setBusy(true);
    const { error } = isNew
      ? await api.createList(title.trim(), caseType.trim() || null)
      : await api.updateList(list.id, { title: title.trim(), case_type: caseType.trim() || null });
    setBusy(false);
    if (error) return notify(api.humanError(error), { type: "error" });
    notify(isNew ? "List created" : "List saved", { type: "success" });
    onSaved(); onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>{isNew ? "New list" : "Edit list"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus placeholder="e.g. probate-2026-07" /></div>
          <div className="space-y-1"><Label className="text-xs">Case type / niche (optional)</Label><Input value={caseType} onChange={(e) => setCaseType(e.target.value)} placeholder="probate, foreclosure…" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !title.trim()}>{busy ? "Saving…" : isNew ? "Create list" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ListsTab = ({ onOpenList }: { onOpenList: (id: string) => void }) => {
  const notify = useNotify();
  const [lists, setLists] = useState<ListRow[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [editing, setEditing] = useState<any | "new" | null>(null);
  const reload = () => api.loadLists().then(setLists);
  useEffect(() => { reload(); api.canManageTagsLists().then(setCanManage); }, []);
  const del = async (l: ListRow) => {
    const msg = l.total_records > 0
      ? `${l.total_records} lead(s) are on "${l.title}" — they won't be deleted, just unlisted. Delete the list?`
      : `Delete list "${l.title}"?`;
    if (!confirm(msg)) return;
    const { error } = await api.deleteList(l.id);
    if (error) return notify(api.humanError(error), { type: "error" });
    notify("List deleted", { type: "success" }); reload();
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">Lists group raw leads for broad comms + campaign assignment (D55).</div>
        {canManage && <Button size="sm" onClick={() => setEditing("new")}><Plus className="mr-1 h-4 w-4" />New list</Button>}
      </div>
      {!canManage && <PermBanner />}
      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>List Title</TableHead><TableHead>Total</TableHead>
              <TableHead>Mail</TableHead><TableHead>Calls</TableHead><TableHead>SMS</TableHead><TableHead>RVM</TableHead>
              <TableHead>Leads</TableHead><TableHead>Offers</TableHead><TableHead>U.C</TableHead><TableHead>Deals</TableHead>{canManage && <TableHead></TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {lists.length === 0 ? <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-8">No lists yet.</TableCell></TableRow> :
                lists.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell><button className="text-blue-700 dark:text-blue-400 hover:underline font-semibold" onClick={() => onOpenList(l.id)}>{l.title}</button></TableCell>
                    <TableCell>{l.total_records}</TableCell>
                    <TableCell>{l.direct_mail}</TableCell><TableCell>{l.cold_calling}</TableCell><TableCell>{l.sms}</TableCell>
                    <TableCell className="text-muted-foreground" title="No RVM source yet">{l.rvm}</TableCell>
                    <TableCell>{l.leads_converted}</TableCell><TableCell>{l.offers_made}</TableCell><TableCell>{l.under_contract}</TableCell><TableCell>{l.deals}</TableCell>
                    {canManage && <TableCell><div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => setEditing(l)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => del(l)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                    </div></TableCell>}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </Card>
      <p className="text-xs text-muted-foreground">RVM is a placeholder (no source yet). Conversions read promoted raw leads → S&A deals.</p>
      {editing !== null && <ListEditor list={editing} onClose={() => setEditing(null)} onSaved={reload} />}
    </div>
  );
};

// ---- Page shell -------------------------------------------------------------
const ListStackingPage = () => {
  const [tab, setTab] = useState("addresses");
  const [selectedList, setSelectedList] = useState<string | null>(null);
  const openList = (id: string) => { setSelectedList(id); setTab("addresses"); };
  return (
    <div className="p-2 max-w-7xl">
      <h1 className="text-[22px] font-extrabold tracking-tight flex items-center gap-2"><Layers className="h-5 w-5 text-teal-600" /> List Stacking</h1>
      <p className="text-sm text-muted-foreground mb-3">Where raw leads arrive and get organized — arrive, review, list, tag.</p>
      <TabBar accent="teal" className="mb-4" value={tab} onChange={setTab} tabs={[
        { value: "dashboard", label: "Dashboard" },
        { value: "addresses", label: "Property Addresses" },
        { value: "import", label: "Import Log" },
        { value: "tags", label: "Tags" },
        { value: "lists", label: "Lists" },
      ]} />
      {tab === "dashboard" && <DashboardTab />}
      {tab === "addresses" && <PropertyAddressesTab selectedList={selectedList} />}
      {tab === "import" && <ImportLogTab />}
      {tab === "tags" && <TagsTab />}
      {tab === "lists" && <ListsTab onOpenList={openList} />}
    </div>
  );
};

ListStackingPage.path = "/list-stacking";
export default ListStackingPage;
